import {useId, useMemo} from 'react';

import {useSkeletonReveal} from '../../hooks';
import {cn, flattenOptionGroups} from '../../lib/utils';
import {Field, FieldSkeleton} from '../Field';
import {Icon} from '../Icon';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '../shadcn/select';

import type {FieldProps} from '../Field';
import type {ComponentProps, ReactNode} from 'react';

export type FieldSelectOption = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  badge?: ReactNode;
};

export type FieldSelectGroup = {
  groupLabel: string;
  options: FieldSelectOption[];
};

export interface FieldSelectProps extends Omit<
  ComponentProps<typeof Select>,
  'children' | 'onValueChange'
> {
  // Field props
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  orientation?: FieldProps['orientation'];
  required?: boolean;
  labelSuffix?: ReactNode;
  // Select content
  placeholder?: string;
  options?: FieldSelectOption[];
  groups?: FieldSelectGroup[];
  badge?: ReactNode;
  /** Corner badge rendered opposite the label — e.g. an AI generating/filled indicator. */
  cornerBadge?: ReactNode;
  /** Shows a pulsing skeleton in place of the value while it's being loaded/generated. */
  isLoading?: boolean;
  // Escape hatches
  id?: string;
  /** Class on the Field root — override e.g. to `"*:w-fit"` to cancel vertical's `*:w-full`. */
  fieldClassName?: string;
  triggerClassName?: string;
  triggerContent?: ReactNode;
  /** Called with the selected string value (never null) */
  onValueChange?: (value: string) => void;
  readOnly?: boolean;
  /** Disables the select and marks it with a lock (next to the label and in place of the chevron). */
  isLocked?: boolean;
  lockedTooltip?: FieldProps['lockedTooltip'];
  lockedLabel?: FieldProps['lockedLabel'];
  /** Popup alignment relative to the trigger. Defaults to "center"; use "start" with a content-hugging trigger. */
  align?: ComponentProps<typeof SelectContent>['align'];
}

export function FieldSelect({
  label,
  description,
  error,
  orientation,
  required,
  labelSuffix,
  placeholder,
  options,
  groups,
  badge,
  cornerBadge,
  isLoading,
  id,
  fieldClassName,
  triggerClassName,
  triggerContent,
  onValueChange,
  readOnly,
  isLocked,
  lockedTooltip,
  lockedLabel,
  align,
  ...selectProps
}: FieldSelectProps) {
  const generatedId = useId();
  const resolvedId = id ?? generatedId;
  const {showSkeleton, onFocus: onRevealFocus} = useSkeletonReveal(isLoading);

  const resolvedItems = useMemo(() => flattenOptionGroups(groups, options), [groups, options]);

  return (
    <Field
      className={fieldClassName}
      label={label}
      description={description}
      error={error}
      htmlFor={resolvedId}
      orientation={orientation}
      required={required}
      labelSuffix={labelSuffix}
      cornerBadge={cornerBadge}
      isLocked={isLocked}
      lockedTooltip={lockedTooltip}
      lockedLabel={lockedLabel}
    >
      <Select
        items={resolvedItems}
        onValueChange={
          onValueChange
            ? (v: unknown) => {
                if (typeof v === 'string') onValueChange(v);
              }
            : undefined
        }
        {...selectProps}
        disabled={selectProps.disabled || isLocked}
        {...(readOnly ? {open: false, onOpenChange: () => {}} : {})}
      >
        <SelectTrigger
          id={resolvedId}
          aria-invalid={!!error || undefined}
          className={cn('relative', triggerClassName)}
          onFocus={onRevealFocus}
          icon={isLocked ? <Icon name="lock" /> : undefined}
        >
          <div className={cn('flex-1', showSkeleton && 'invisible')}>
            {triggerContent ?? <SelectValue placeholder={placeholder} />}
          </div>
          {showSkeleton && <FieldSkeleton className="inset-s-3 top-1/2 -translate-y-1/2" />}
          {badge}
        </SelectTrigger>
        <SelectContent align={align}>
          {groups
            ? groups.map(group => (
                <SelectGroup key={group.groupLabel}>
                  <SelectLabel>{group.groupLabel}</SelectLabel>
                  {group.options.map(opt => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.disabled}
                      badge={opt.badge}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))
            : options?.map(opt => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                  badge={opt.badge}
                >
                  {opt.label}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
    </Field>
  );
}
